/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Components.Controls {
    'use strict';

    import IIdentity = Meevo.Components.Common.IIdentity;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import SecurityAccessEnum = Meevo.Model.SecurityAccessEnum;

    enum EmployeeAvailability {
        Active = 1,
        Inactive = 2,
        Both = 3,
        Default = Active,
    }

    interface IEmployeeModel {
        id: string | Guid,
        displayName: string,
        active: boolean,
        objectState?: ObjectStateEnum,
        selected: boolean
    }

    export interface IGetAllEmployeeSettings {
        byCategories: boolean;
    }

    interface IEmployeeMoverController {
        securityAccess: SecurityAccessEnum;
        availabilityFilter: EmployeeAvailability;
        ngModel: angular.INgModelController;
        centralOffice: boolean;
        withCategories: boolean;
        categoriesList: string[];
        // hierarchy control's variables
        hierarchyBusinessType: Guid | string;
        hierarchyId: string;
        hierarchyBranchItem: string;
        hierarchyLocationList: Array<any>;
        defaultValue: Array<any>;
        fetching: boolean;
        error: string;
        disabled: boolean; // hierarchy form validity
        securityDisabled: boolean;

        all: IEmployeeModel[];
        available: IEmployeeModel[];
        selected: IEmployeeModel[];
        availableSelection: string[];
        selectedSelection: string[];

        rangeFilterYear: number;
        rangeFilterRange: string;


        initModelValue: any;
        isNullValue: boolean;
        isAllEmployeeSelected: boolean;
        hierarchyCriteria: Object;
        customCriteria: Object;
        canceller: ng.IDeferred<any>;

        init(): void;

        setHierarchyWatchers(): void;

        createHierarchyCriteria(newVal: any, oldVal: any): void;

        populate(employees: any[]): void;

        setWatchers(): void;

        select(): void;

        selectAll(): void;

        deselect(): void;

        deselectAll(): void;

        getAvailable(): IEmployeeModel[];

        getSelected(): IEmployeeModel[];

        reselect(): void;

        getAllEmployees(): ng.IPromise<any>;

        checkAllCallback(): void;

        setModelValue(): void;
    }

    export class EmployeeMoverController implements IEmployeeMoverController {
        static $inject = [
            '$scope',
            '$q',
            'meevo.components.common.DataService',
            'meevo.components.common.IdentityService',
            'meevo.components.busy.BusyService',
            '$timeout'
        ];

        static buildDisplayName(employee: IIdentity): string {
            return employee.displayName;
        }

        securityAccess: SecurityAccessEnum;
        availabilityFilter: EmployeeAvailability;
        centralOffice: boolean;
        withCategories: boolean;
        categoriesList: string[];

        // comes from directive's link function
        ngModel: angular.INgModelController;
        hierarchyBusinessType: Guid | string;
        hierarchyId: string;
        hierarchyBranchItem: string;
        hierarchyLocationList: Array<any>;
        defaultValue: Array<any>;
        fetching: boolean = false;
        error: string = null;

        all: IEmployeeModel[] = [];
        available: IEmployeeModel[] = [];
        selected: IEmployeeModel[] = [];
        rangeFilterYear: number;
        rangeFilterRange: string;
        fetchOnStart?: boolean;
        filterList: {label: string; value: EmployeeAvailability;}[];

        availableSelection: any[] = [];
        selectedSelection: any[] = [];
        hierarchyCriteria: Object = {};
        customCriteria: Object = {};
        canceller: ng.IDeferred<any> = null;

        initModelValue: any = [];
        isNullValue: boolean = true;
        emptyAsAll: boolean;
        isAllEmployeeSelected: boolean = true;

        disabled: boolean; // hierarchy form validity
        securityDisabled: boolean = false;
        checkBoxContentKey: string;

        constructor(protected $scope: angular.IScope,
                    protected $q: angular.IQService,
                    protected dataService: Meevo.Components.BaseClasses.IDataService,
                    protected identityService: Meevo.Components.Common.IIdentityService,
                    protected busyService: Meevo.Components.Busy.IBusyService,
                    protected $timeout: angular.ITimeoutService) {
        }

        init(): void {
            let currentUser = this.identityService.current();

            this.filterList = this.filterList || [
                {label: 'LABEL_ACTIVE_EMPLOYEES', value: EmployeeAvailability.Active},
                {label: 'LABEL_INACTIVE_EMPLOYEES', value: EmployeeAvailability.Inactive},
                {label: 'LABEL_ACTIVE_AND_INACTIVE_EMPLOYEES', value: EmployeeAvailability.Both},
            ];

            this.hierarchyCriteria = {
                businessTypeList: [this.identityService.businessTypeId],
                locationList: [this.identityService.locationId],
                hierarchyId: null,
                branchId: null
            };

            if (this.centralOffice) {
                this.setHierarchyWatchers();
            }

            if (this.defaultValue && this.defaultValue.length) {
                this.isAllEmployeeSelected = false;
                this.getAllEmployees();
                this.$timeout(() => {
                    this.all = this.defaultValue;
                });
            }

            if (!angular.isDefined(this.availabilityFilter)) {
                this.availabilityFilter = EmployeeAvailability.Default;
            }

            this.setWatchers();

            if (this.securityAccess === SecurityAccessEnum.Grant) {
                this.ngModel.$setViewValue(this.initModelValue);
            } else {
                this.securityDisabled = true;
                this.availabilityFilter = EmployeeAvailability.Both;
                this.isAllEmployeeSelected = false;

                this.initModelValue = [currentUser.userId];
                this.ngModel.$setViewValue([currentUser.userId]);
                this.all = [
                    {
                        id: currentUser.userId,
                        displayName: EmployeeMoverController.buildDisplayName(currentUser),
                        objectState: ObjectStateEnum.Active,
                        active: true,
                        selected: true
                    }
                ];

                this.$timeout(() => {
                    this.populate([
                        {
                            id: currentUser.userId,
                            displayName: EmployeeMoverController.buildDisplayName(currentUser),
                            objectState: ObjectStateEnum.Active,
                            active: true,
                            selected: true
                        }
                    ]);
                });
            }

            if (this.fetchOnStart) {
                this.getAllEmployees();
            }
        }

        /**
         * Set watchers on Hierarchy control's variables in CO mode
         */
        setHierarchyWatchers(): void {
            this.$scope.$watch(() => this.hierarchyBusinessType, this.createHierarchyCriteria.bind(this));
            this.$scope.$watch(() => this.hierarchyBranchItem, this.createHierarchyCriteria.bind(this));
            this.$scope.$watch(() => this.hierarchyLocationList, this.createHierarchyCriteria.bind(this), true);
        }

        /**
         * Create hierarchy criteria for getting employees
         * @param {any} newValue
         * @param {any} oldValue
         */
        createHierarchyCriteria(newValue, oldValue): void {
            if (!angular.isDefined(newValue) || angular.equals(newValue, oldValue)) return;

            if ((this.hierarchyId && !this.hierarchyBranchItem) || (!this.hierarchyId && this.hierarchyBranchItem)) return;

            this.hierarchyCriteria = {
                businessTypeList: this.hierarchyBusinessType,
                locationList: this.hierarchyLocationList,
                hierarchyId: this.hierarchyId,
                branchId: this.hierarchyBranchItem
            };

            if (this.securityAccess === SecurityAccessEnum.Grant) {
                if (this.isAllEmployeeSelected) {
                    this.ngModel.$setViewValue(this.initModelValue);
                } else {
                    this.availabilityFilter = EmployeeAvailability.Active;
                    this.getAllEmployees();
                }
            }
        }

        populate(employees: any[], settings?: IGetAllEmployeeSettings): void {
            let selectEmployee: boolean = false;

            if (employees === null) {
                return;
            }

            if (!angular.isArray(this.ngModel.$viewValue)) {
                this.ngModel.$setViewValue([]);
            }

            selectEmployee = settings && settings.byCategories && this.categoriesList && this.categoriesList.length > 0;

            this.all = employees
                .map((employee): IEmployeeModel => {
                    const {id, objectState} = employee;
                    const existingEmployee = _.find(this.all, (emp: IEmployeeModel) => emp.id === id);
                    let selectThisEmployee: boolean;

                    switch (this.availabilityFilter) {
                        case EmployeeAvailability.Active:
                            selectThisEmployee = selectEmployee && employee.objectState === ObjectStateEnum.Active;
                            break;
                        case EmployeeAvailability.Inactive:
                            selectThisEmployee = selectEmployee && employee.objectState === ObjectStateEnum.Inactive;
                            break;
                        case EmployeeAvailability.Both:
                            selectThisEmployee = selectEmployee && (employee.objectState === ObjectStateEnum.Active || employee.objectState === ObjectStateEnum.Inactive);
                            break;
                    }


                    return {
                        id,
                        displayName: EmployeeMoverController.buildDisplayName(employee),
                        active: objectState === Meevo.Model.ObjectStateEnum.Active,
                        selected: existingEmployee ? existingEmployee.selected : selectThisEmployee
                    }
                });
        }

        setWatchers(): void {
            this.$scope.$watch(() => this.ngModel.$modelValue, (newVal, oldVal) => {
                if (newVal && !angular.equals(newVal, oldVal) && !this.isNullValue) {
                    this.reselect();
                }
            });

            this.$scope.$watch(() => this.all, (newVal, oldVal) => {
                if (newVal && !angular.equals(newVal, oldVal)) {
                    this.selected = this.getSelected();
                    this.available = this.getAvailable();
                    this.setModelValue();
                }
            }, true);

            this.$scope.$watch(() => this.availabilityFilter, (newVal, oldVal) => {
                if (newVal && !angular.equals(newVal, oldVal)) {
                    this.selected = this.getSelected();
                    this.available = this.getAvailable();
                    this.setModelValue();
                }
            });

            this.$scope.$watch(() => this.rangeFilterRange, (newVal, oldVal) => {
                // TODO: apply rangeFilter to the request body and refresh employees
            });

            // if employee mover is used with classification mover
            if (this.withCategories) {
                this.$scope.$watch(() => this.categoriesList, (newVal, oldVal) => {
                    if (!angular.equals(newVal, oldVal)) {
                        if (this.securityAccess === SecurityAccessEnum.Grant) {
                            if (this.isAllEmployeeSelected) {
                                this.ngModel.$setViewValue(this.initModelValue);
                            } else {
                                if (oldVal && oldVal.length === 0 || newVal && newVal.length === 0) {
                                    this.all = [];
                                }
                                this.getAllEmployees({
                                    byCategories: true
                                });
                            }

                        }
                    }
                });
            }

            this.$scope.$watch(() => this.customCriteria, (newVal, oldVal) => {
                if (newVal && !angular.equals(newVal, oldVal)) {
                    if (!this.isAllEmployeeSelected) {
                        this.getAllEmployees();
                    }
                }
            }, true);
        }

        select(): void {
            this.all.forEach((e: IEmployeeModel) => {
                if (this.availableSelection.indexOf(e.id) > -1) e.selected = true;
            })
        };

        selectAll(): void {
            this.all.forEach((e: IEmployeeModel) => {
                if (e.active && this.availabilityFilter === EmployeeAvailability.Active ||
                    !e.active && this.availabilityFilter === EmployeeAvailability.Inactive ||
                    this.availabilityFilter === EmployeeAvailability.Both
                ) {
                    e.selected = true;
                }
            })
        };

        deselect(): void {
            this.all.forEach((e: IEmployeeModel) => {
                if (this.selectedSelection.indexOf(e.id) > -1) e.selected = false;
            })
        };

        deselectAll(): void {
            this.all.forEach((e: IEmployeeModel) => {
                e.selected = false;
            })
        };

        getAvailable(): IEmployeeModel[] {
            switch (this.availabilityFilter) {
                case EmployeeAvailability.Active:
                    return this.all.filter((e: IEmployeeModel) => !e.selected && e.active);
                case EmployeeAvailability.Inactive:
                    return this.all.filter((e: IEmployeeModel) => !e.selected && !e.active);
                case EmployeeAvailability.Both:
                    return this.all.filter((e: IEmployeeModel) => !e.selected)
            }
        }

        getSelected(): IEmployeeModel[] {
            return this.all.filter((e: IEmployeeModel) => e.selected);
        }

        reselect() {
            if (this.all.some((e: IEmployeeModel) => this.ngModel.$viewValue.indexOf(e.id) > -1)) {
                this.all.forEach((e: IEmployeeModel) => e.selected = this.ngModel.$viewValue.indexOf(e.id) > -1);
            } else {
                this.all.forEach((e: IEmployeeModel) => e.selected = false);
                this.ngModel.$setViewValue([]);
            }
        }

        getAllEmployees(settings?: IGetAllEmployeeSettings): ng.IPromise<any> {
            this.fetching = true;
            let criteria: Object = angular.extend(this.hierarchyCriteria, {
                objectStateEnumList: [ObjectStateEnum.Active, ObjectStateEnum.Inactive],
                employeeCategoryList: this.categoriesList || [],
                genderEnumList: [92, 93, 94, 2035],
                displayName: null,
                fieldOrderBy: 'DisplayName',
                orderAscDesc: 'Ascending'
            }, this.customCriteria);

            if (this.canceller) {
                this.canceller.resolve();
            }

            this.canceller = this.$q.defer();

            this.busyService.start();
            return this.dataService.getListPaged('Report/Employee', {criteria}, null, null, {
                timeout: this.canceller.promise
            })
                .then(employees => {
                    this.populate(employees, settings);
                    this.error = null;
                })
                .catch(err => {
                    this.error = 'Could not fetch employee list. Employee Mover control can not be initialized.';
                })
                .finally(() => {
                    this.fetching = false;
                    this.busyService.stop();
                    this.canceller = null;
                })
        }

        setModelValue(): void {
            let modelValue: Array<string | Guid>;

            this.isNullValue = !this.selected.length || this.selected.length === this.all.length;
            if (this.emptyAsAll && this.selected.length === 0) {
                modelValue = this.all.map((e) => e.id);
            } else if (this.isNullValue) {
                modelValue = this.initModelValue;
            } else {
                modelValue = this.selected.map((e: IEmployeeModel) => e.id);
            }

            this.ngModel.$setViewValue(modelValue);
        }

        /**
         * Callback for check/uncheck all employee
         * */
        checkAllCallback(): void {
            let settings: IGetAllEmployeeSettings = {
                byCategories: true
            };

            if (this.isAllEmployeeSelected) {
                this.populate([], settings);
            } else {
                this.getAllEmployees(settings);
            }
        }

        get availabilityEnum(): any {
            return EmployeeAvailability;
        }
    }
}
